import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, X, Search } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import { contactsAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';
import { useAuth } from '../contexts/AuthContext.jsx';

// The Executive-Admin's personal CONTACT DIRECTORY editor (name → number). This is
// the address book the assistant resolves "call John and tell him…" against. Aaron
// seeds it on the onboarding call and can add/edit here anytime. Tenant-scoped CRUD
// over the contacts table (see core/src/routes/contacts.js).

const EMPTY = { name: '', phone: '', email: '', category: '', notes: '' };

export default function Contacts() {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin'; // writes are admin-only (server 403s otherwise)
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = async (q = '') => {
    setLoading(true);
    setError('');
    try {
      const r = await contactsAPI.list(q);
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load contacts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const startEdit = (c) =>
    setEditing((e) => ({
      ...e,
      [c.id]: { name: c.name || '', phone: c.phone || '', email: c.email || '', category: c.category || '', notes: c.notes || '' }
    }));
  const cancelEdit = (id) =>
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    if (!draft?.name?.trim()) return;
    try {
      const r = await contactsAPI.update(id, {
        name: draft.name.trim(),
        phone: draft.phone.trim() || null,
        email: draft.email.trim() || null,
        category: draft.category.trim() || null,
        notes: draft.notes.trim() || null
      });
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
    } catch (err) {
      setError(err.body?.message || err.message || 'Save failed.');
    }
  };

  const remove = async (id, name) => {
    if (!confirm(`Remove ${name || 'this contact'} from your directory?`)) return;
    try {
      await contactsAPI.remove(id);
      setItems((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(err.message || 'Delete failed.');
    }
  };

  const create = async () => {
    if (!creating.name.trim()) return;
    try {
      const r = await contactsAPI.create({
        name: creating.name.trim(),
        phone: creating.phone.trim() || null,
        email: creating.email.trim() || null,
        category: creating.category.trim() || null,
        notes: creating.notes.trim() || null
      });
      setItems((list) => [r.data, ...list]);
      setCreating(EMPTY);
      setAdding(false);
    } catch (err) {
      setError(err.body?.message || err.message || 'Create failed.');
    }
  };

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle="Your assistant's directory — the people it can call for you by name."
        actions={
          canWrite && !adding && (
            <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
              <Plus size={14} /> Add contact
            </button>
          )
        }
      />

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="mb-4 relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
        <input
          className="field-input pl-9"
          placeholder="Search name, number, or category…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {adding && (
        <div className="mb-4 card border border-alfred-gold/30 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              className="field-input"
              placeholder="Name (e.g. John Marsh)"
              value={creating.name}
              onChange={(e) => setCreating({ ...creating, name: e.target.value })}
              autoFocus
            />
            <input
              className="field-input"
              placeholder="Phone (e.g. 512-555-0134)"
              value={creating.phone}
              onChange={(e) => setCreating({ ...creating, phone: e.target.value })}
            />
            <input
              className="field-input"
              type="email"
              placeholder="Email (optional, e.g. john@acme.com)"
              value={creating.email}
              onChange={(e) => setCreating({ ...creating, email: e.target.value })}
            />
            <input
              className="field-input"
              placeholder="Category (optional, e.g. Attorney)"
              value={creating.category}
              onChange={(e) => setCreating({ ...creating, category: e.target.value })}
            />
            <input
              className="field-input"
              placeholder="Notes (optional)"
              value={creating.notes}
              onChange={(e) => setCreating({ ...creating, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <section className="card">
        {loading ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-stone-500">
            {query ? 'No contacts match your search.' : 'No contacts yet. Add the people your assistant should be able to call.'}
          </p>
        ) : (
          <ul className="divide-y divide-stone-900">
            {items.map((c) => {
              const draft = editing[c.id];
              if (draft) {
                return (
                  <li key={c.id} className="py-4 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        className="field-input"
                        placeholder="Name"
                        value={draft.name}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], name: e.target.value } }))}
                      />
                      <input
                        className="field-input"
                        placeholder="Phone"
                        value={draft.phone}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], phone: e.target.value } }))}
                      />
                      <input
                        className="field-input"
                        type="email"
                        placeholder="Email"
                        value={draft.email}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], email: e.target.value } }))}
                      />
                      <input
                        className="field-input"
                        placeholder="Category"
                        value={draft.category}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], category: e.target.value } }))}
                      />
                      <input
                        className="field-input"
                        placeholder="Notes"
                        value={draft.notes}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], notes: e.target.value } }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={() => saveEdit(c.id)}>
                        <Save size={14} /> Save
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => cancelEdit(c.id)}>
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </li>
                );
              }
              return (
                <li key={c.id} className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-stone-100 font-medium">{c.name}</span>
                        {c.category && (
                          <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">
                            {c.category}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-stone-400">
                        {formatPhone(c.phone)}
                        {c.email && <span className="ml-2 text-stone-500">· {c.email}</span>}
                      </div>
                      {c.notes && <p className="mt-1 text-sm text-stone-500 whitespace-pre-wrap">{c.notes}</p>}
                    </div>
                    {canWrite && (
                      <div className="shrink-0 flex items-center gap-1">
                        <button type="button" className="btn-ghost" onClick={() => startEdit(c)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-ghost text-red-300 hover:text-red-200"
                          onClick={() => remove(c.id, c.name)}
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
